import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { IS_JEST, DEV_MODE } from '../../utils/Constants';
import useAuthUtils from '../../utils/useAuthUtils';
import { faker } from '@faker-js/faker';
import Button from '../../components/controls/Button';
import { TextParagraph } from '../../components/controls/Typography/TextBody';
import Heading from '../../components/controls/Typography/Heading';
import { From } from '../../components/controls/Form';

import './extras.less';

export const ReuseGuestAccessSubmission = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuthUtils();

  const {
    title = null,
    applicantFirstName = null,
    applicantLastName = null,
    formName = null,
    evalType = null,
    hasAccount = null,
    pdfDownloadLink = null,
    isResueActive = null,
  } = location.state ?? (IS_JEST || DEV_MODE ? {
    title: faker.lorem.words(5),
    applicantFirstName: faker.internet.userName(),
    applicantLastName: faker.internet.userName(),
    formName: faker.commerce.productName(),
    evalType: faker.company.name(),
    hasAccount: faker.number.int({ min: 0, max: 1 }) === 1,
    pdfDownloadLink: faker.internet.url(),
    isResueActive: faker.number.int({ min: 0, max: 1 }) === 1,
  } : {});

  /**
   * @param {{ flowOfReuse:boolean }=} params
   */
  const goToLogin = params => {
    const { flowOfReuse = false } = params || {};
    auth.flows.reUseEvalGuest.set(flowOfReuse ? auth.guest : null);
    auth.doLogin();
  };

  /**
   * @param {{ flowOfReuse:boolean }=} params
   */
  const goToRegister = params => {
    const { flowOfReuse = false } = params || {};
    auth.flows.reUseEvalGuest.set(flowOfReuse ? auth.guest : null);
    auth.doRegister();
  };

  const discardSubmission = () => {
    auth.doLogout({
      onComplete: () => window.close()
    });
  };

  const downloadPdf = () => {
    window.open(pdfDownloadLink, '_blank');
  };

  useEffect(() => {
    document.title = 'Letters Portal - Guest Access';
    DEV_MODE || title ? void 0 : navigate('/notFound');
  }, []);

  return (
    <main className="lsn-page lsn-page-success lsn-page-reuse-guest-access-submission">
      <Heading className="text-center no-focus-outline" tabIndex={0}>
        {title}
      </Heading>

      {pdfDownloadLink ? (
        <div className="lsn-download-btn text-center">
          <Button
            primary
            ghost
            icon={<i className="far fa-download"></i>}
            onClick={() => downloadPdf()}
          >
            Download a pdf
          </Button>
        </div>
      ) : null}

      <From
        title={isResueActive ? void 0 : 'Reuse this recommendation for all future requests from this applicant?'}
        hasBorder
        isNarrow
        isButtonsStack
        buttons={
          <>
            {isResueActive ? (
              <Button
                primary
                onClick={() => goToLogin()}
              >
                Go to Login
              </Button>) :

              hasAccount ? (
                <Button
                  primary
                  onClick={() => goToLogin({ flowOfReuse: true })}
                >
                  Log In / Reuse Recommendation
                </Button>
              ) : (
                <Button
                  primary
                  onClick={() => goToRegister({ flowOfReuse: true })}
                >
                  Create Account / Reuse Recommendation
                </Button>
              )}
            <Button
              ghost
              onClick={() => discardSubmission()}
            >
              No thanks, close this window.
            </Button>
          </>
        }
      >
        {isResueActive ? (
          <>
            <TextParagraph>
              This recommendation is being reused for all future recommendation requests from{' '}
              <b>{applicantFirstName} {applicantLastName}</b>.
            </TextParagraph>
            <TextParagraph>
              If you would like to make changes to your reuse settings
              you must log in below.
            </TextParagraph>
          </>
        ) : (
          <>
            <TextParagraph>
              You can choose to automatically reuse this recommendation for all outstanding and future{' '}
              <b>{evalType}</b> recommendation requests from{' '}
              <b>
                {applicantFirstName} {applicantLastName}
              </b>{' '}
              for <b>{formName}</b>.
            </TextParagraph>

            <TextParagraph>
              If you choose to reuse this recommendation, you can't view or edit it moving forward; it will be
              automatically submitted as-is on your behalf.
            </TextParagraph>

            <TextParagraph bold>
              To reuse this recommendation, you'll need to
              {hasAccount ? ' log in' : ' create an account'}.
            </TextParagraph>
          </>
        )}
      </From>
    </main >
  );
};

export default ReuseGuestAccessSubmission;
